import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Navigate } from "react-router-dom";
import { BlogPostForm } from "@/components/blog/BlogPostForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface BlogPost {
  id: string;
  title: string;
  description: string;
  content: string;
  date: string;
  image_url: string;
  category: string;
  slug: string;
  published: boolean;
}

const BlogAdmin = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState<BlogPost | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setIsAdmin(false);
      return;
    }

    // Check if user has admin role using the new role system
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .maybeSingle();

    const hasAdminRole = !!roles;
    setIsAdmin(hasAdminRole);
    if (hasAdminRole) {
      fetchBlogPosts();
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load blog posts. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePublished = async (postId: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('blogs')
        .update({ published: !currentState })
        .eq('id', postId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Post ${!currentState ? 'published' : 'unpublished'} successfully`,
      });

      fetchBlogPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update post status.",
      });
    }
  };

  const deletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', postId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Post deleted successfully",
      });

      fetchBlogPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete post.",
      });
    }
  };

  const handleSavePost = async (data: {
    title: string;
    description: string;
    content: string;
    category: string;
  }) => {
    try {
      const slug = data.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      if (currentPost) {
        const { error } = await supabase
          .from('blogs')
          .update({
            ...data,
            slug,
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentPost.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Post updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('blogs')
          .insert([{
            ...data,
            slug,
            date: new Date().toISOString(),
          }]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Post created successfully",
        });
      }

      setIsEditing(false);
      setCurrentPost(null);
      fetchBlogPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save post.",
      });
    }
  };

  if (isAdmin === null) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Blog Admin</h1>
          <Button onClick={() => {
            setCurrentPost(null);
            setIsEditing(true);
          }}>New Post</Button>
        </div>

        <div className="grid gap-6">
          {blogPosts.map((post) => (
            <Card key={post.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="mb-2">{post.title}</CardTitle>
                    <div className="text-sm text-muted-foreground">
                      {format(new Date(post.date), 'MMMM d, yyyy')} · {post.category}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={post.published}
                        onCheckedChange={() => togglePublished(post.id, post.published)}
                      />
                      <span className="text-sm">
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setCurrentPost(post);
                        setIsEditing(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => deletePost(post.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{post.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader className="pb-2">
            <DialogTitle>{currentPost ? 'Edit Post' : 'New Post'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-80px)] pr-4">
            <div className="pb-8">
              <BlogPostForm
                initialData={currentPost || undefined}
                onSubmit={handleSavePost}
              />
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogAdmin;
